import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, AlertCircle, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Task } from "@/types/workspace";
import { useLocation } from "wouter";

interface UpcomingTasksProps {
    tasks: Task[];
}

const priorityColors: Record<Task["priority"], string> = {
    urgent: "bg-red-500 hover:bg-red-600 text-white",
    high: "bg-orange-500 hover:bg-orange-600 text-white",
    medium: "bg-yellow-500 hover:bg-yellow-600 text-white",
    low: "bg-blue-500 hover:bg-blue-600 text-white",
};

export function UpcomingTasks({ tasks }: UpcomingTasksProps) {
    const [, setLocation] = useLocation();

    const handleTaskClick = (task: Task) => {
        if (task.claimId) {
            setLocation(`/claims/${task.claimId}`);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="h-full min-w-0"
        >
            <Card className="h-[480px] min-w-0 flex flex-col overflow-hidden">
                <CardHeader className="min-w-0 shrink-0 [overflow-wrap:anywhere]">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <div className="p-2 bg-muted rounded-lg">
                            <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                        Upcoming Tasks
                    </CardTitle>
                    <CardDescription>Your next tasks to complete</CardDescription>
                </CardHeader>
                <CardContent className="min-h-0 min-w-0 flex-1 overflow-hidden [overflow-wrap:anywhere]">
                    <div className="space-y-3 h-full overflow-y-auto overflow-x-hidden pr-2">
                        {tasks.length > 0 ? (
                            tasks.map((task) => (
                                <div
                                    key={task.id}
                                    className={cn(
                                        "flex min-w-0 items-start gap-3 p-3 border border-border rounded-lg overflow-hidden hover:bg-muted/50 transition-colors duration-100 cursor-pointer group"
                                    )}
                                    onClick={() => handleTaskClick(task)}
                                >
                                    <div className="mt-0.5 shrink-0 transition-transform duration-100 group-hover:scale-110">
                                        {task.status === "pending" ? (
                                            <AlertCircle className="h-4 w-4 text-amber-500" />
                                        ) : (
                                            <CheckCircle className="h-4 w-4 text-muted-foreground" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground line-clamp-3 [overflow-wrap:anywhere]" title={task.title}>
                                            {task.title}
                                        </p>
                                        {task.claimNumber && (
                                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 [overflow-wrap:anywhere]" title={`#${task.claimNumber}${task.insuredName ? ` • ${task.insuredName}` : ""}`}>
                                                #{task.claimNumber}{task.insuredName && ` • ${task.insuredName}`}
                                            </p>
                                        )}
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                                            {task.dueDate && (
                                                <p className="text-xs text-muted-foreground">
                                                    Due: {format(new Date(task.dueDate), "MMM d, yyyy")}
                                                </p>
                                            )}
                                            <Badge className={cn("h-4 shrink-0 px-1.5 py-0 text-[10px] leading-none", priorityColors[task.priority || "medium"])}>
                                                {task.priority || "medium"}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center">
                                    <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2 opacity-20" />
                                    <p className="text-muted-foreground">No pending tasks</p>
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
